/*
 * Copyright (c) Nexign, JSC, 2019-2021.
 */
/**
 *
 *
 * @author Evgenij Tyan
 * @author Aleksandr Perkhunov
 * @author Yakov Gushchin
 * @version 1.11.0
 * @since 1.0.0
 */
import React, { ChangeEvent, ReactNode, RefObject, useEffect, useRef, useState } from 'react';

import get from 'lodash/get';
import invoke from 'lodash/invoke';

import { Input } from '../../../../../../components/common';
import { KeyboardKeyCodeEnum } from '../../../../../../modules/app/appConstants';
import { ILabelEditorTranslation } from '../../../../../../modules/internationalization/types/corporate';
import ButtonServiceUserDenied from '../../../../../../modules/role/components/ButtonServiceUserDenied';
import { EInputKind } from '../../../../../common/Input/Input';
import {
    CreateLabelButtonTextStyled,
    CreateLabelContainerStyled,
    CreateLabelModalContainerStyled,
} from './styled/LabelEditorCreateStyled';

/**
 * @description Create label button props
 * @interface ISaveAsButtonProps
 * @property {Function} onCreate - save callback
 */
export interface ICreateLabelProps {
    children: ReactNode;
    onCreate: (name: string) => void;
    translation: ILabelEditorTranslation;
}

export const LabelEditorCreate = (props: ICreateLabelProps) => {

    const [inputKind, setInputKind] = useState<EInputKind>(EInputKind.normal);
    const [isModalShown, setModalShow] = useState<boolean>(false);
    const [name, setName] = useState<string>('');
    const modalRef: RefObject<HTMLDivElement> = useRef(null);

    useEffect(() => {
        window.addEventListener('click', handleClickOutside, true);
        return () => {
            window.removeEventListener('click', handleClickOutside, true);
        };
    }, []);

    /**
     * @description Close modal window
     */
    const closeModal = (): void => {
        setModalShow(false);
        setInputKind(EInputKind.normal);
        setName('');
    };
    const toggleModal = (): void => {
        setModalShow(!isModalShown);
    };
    /**
     * @description Check outside click, and close modal window
     * @param {MouseEvent} event
     */
    const handleClickOutside = (event: MouseEvent): void => {
        if (
            get(modalRef, 'current') &&
            !invoke(modalRef, 'current.contains', event.target as Node)
        ) {
            event.stopPropagation();
            closeModal();
        }
    };
    /**
     * @description Change input value handler
     * @param {ChangeEvent<HTMLInputElement>} event - input change event
     */
    const inputChangeHandler = (event: ChangeEvent<HTMLInputElement>): void => {
        event.stopPropagation();
        const name = event.target.value;
        setInputKind(EInputKind.normal);
        setName(name);
    };
    /**
     * @description Create button hanlder
     * if filter name not empty - call onSave and close modal window
     * @param {MouseEvent} event
     */
    const handleButtonClick = (event: React.MouseEvent): void => {
        invoke(event, 'stopPropagation');
        if (name) {
            props.onCreate(name);
            setName('');
            closeModal();
        } else {
            setInputKind(EInputKind.error);
        }
    };

    const keyDownHandler = (event: React.KeyboardEvent) => {
        if (event.keyCode === KeyboardKeyCodeEnum.ENTER) {
            invoke(event, 'stopPropagation');
            if (name) {
                props.onCreate(name);
                setName('');
                closeModal();
            } else {
                setInputKind(EInputKind.error);
            }
        }
    };

    return (
        <CreateLabelContainerStyled
        >
            <CreateLabelButtonTextStyled
                data-name="createLabelButton"
                onClick={toggleModal}>
                {props.children}
            </CreateLabelButtonTextStyled>
            {
                isModalShown &&
                <CreateLabelModalContainerStyled
                    ref={modalRef}
                    onKeyDown={keyDownHandler}
                >
                    <Input
                        autoFocus
                        className="create-label__input"
                        kind={inputKind}
                        maxLength={255}
                        placeholder={props.translation.createPlaceholder}
                        value={name}
                        onChange={inputChangeHandler}
                    />
                    <ButtonServiceUserDenied
                        className="create-label__save-button"
                        kind="mediumNormal"
                        onClick={handleButtonClick}
                    >
                        {props.translation.createLabel}
                    </ButtonServiceUserDenied>
                </CreateLabelModalContainerStyled>
            }
        </CreateLabelContainerStyled>
    );
};

export default LabelEditorCreate;
